import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tag, Search, Plus, Trash2, X, Filter, FileText, FileSpreadsheet,
  RefreshCcw, ChevronDown, ChevronUp, Upload, Download,
  FileOutput, LayoutGrid, CheckCircle, XCircle,
  Layers, List, LayoutList, Calendar, Clock, Hash, Type, Ruler, ToggleLeft,
  BarChart3, Shield, AlignLeft
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { CustomDatePicker } from '../../shared/components/custom';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const DATA_TYPE_BADGE = {
  texte: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  nombre: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  liste: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'booléen': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const AttributsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.article.pages.attributsPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const articlesConfig = useArticlesConfig();

  const DATA_TYPE_OPTIONS = useMemo(
    () => [
      { value: 'texte', label: p('data_type_text'), isDefault: true },
      { value: 'nombre', label: p('data_type_number'), isDefault: true },
      { value: 'liste', label: p('data_type_list'), isDefault: true },
      { value: 'booléen', label: p('data_type_boolean'), isDefault: true },
    ],
    [t]
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'inactif', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [t]
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const getDataTypeLabel = useCallback(
    (dataType) => {
      const map = {
        texte: p('data_type_text'),
        nombre: p('data_type_number'),
        liste: p('data_type_list'),
        'booléen': p('data_type_boolean'),
      };
      return map[dataType] || dataType || p('not_applicable');
    },
    [t]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    data_type: 'texte',
    unit: '',
    is_required: false,
    options_json: '',
    category_scope: '',
    usage_count: '',
    status: 'actif',
    notes: '',
  });

  const [formTab, setFormTab] = useState('base');

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_attributes');
  const [quickFilter, setQuickFilter] = useState('all');
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'السمة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'سمة' });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    loadAttributes();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAttributes = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/api/articles/attributs');
      const data = response?.data ?? response;
      setAttributes(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading attributes:', error);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      window.alert(p('alert_name_required'));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name.trim(),
        data_type: formData.data_type || 'texte',
        unit: formData.unit || null,
        is_required: !!formData.is_required,
        options_json: formData.options_json || null,
        category_scope: formData.category_scope || null,
        usage_count: parseInt(formData.usage_count, 10) || 0,
        status: formData.status || 'actif',
        notes: formData.notes || null,
      };

      if (editingRecord) {
        await localApiAdapter.put(`/api/articles/attributs/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/attributs', payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving attribute:', error);
      notifyError(showNotify, p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      name: record.name || '',
      data_type: record.data_type || 'texte',
      unit: record.unit || '',
      is_required: !!record.is_required,
      options_json: record.options_json || '',
      category_scope: record.category_scope || '',
      usage_count: record.usage_count?.toString() || '0',
      status: record.status || 'actif',
      notes: record.notes || '',
    });
    setFormTab('base');
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`/api/articles/attributs/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        const deleteCount = ids.length;
        await Promise.all(
          selectedRecords.map((id) => localApiAdapter.delete(`/api/articles/attributs/${id}`))
        );
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting attribute:', error);
        notifyError(showNotify, p('alert_save_error'));
      } finally {
        setLoading(false);
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/attributs', () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  

  const resetForm = () => {
    setFormData({
      name: '',
      data_type: 'texte',
      unit: '',
      is_required: false,
      options_json: '',
      category_scope: '',
      usage_count: '',
      status: 'actif',
      notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
    setFormTab('base');
  };

  const formTabs = useMemo(() => ([
    { id: 'base', label: p('section_base'), icon: Hash },
    { id: 'scope_notes', label: p('section_scope'), icon: Layers },
  ]), [p]);

  const handleSelectRecord = (id) => {
    setSelectedRecords(
      selectedRecords.includes(id)
        ? selectedRecords.filter((rId) => rId !== id)
        : [...selectedRecords, id]
    );
  };

  const attributesArray = Array.isArray(attributes) ? attributes : [];

  const stats = {
    total: attributesArray.length,
    actifs: attributesArray.filter((a) => a.status === 'actif').length,
    inactifs: attributesArray.filter((a) => a.status === 'inactif').length,
    obligatoires: attributesArray.filter((a) => a.is_required).length,
    utilisations: attributesArray.reduce((sum, a) => sum + (parseInt(a.usage_count, 10) || 0), 0),
  };

  const mainTabs = [
    { id: 'all_attributes', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Type },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'actif', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactif', name: p('filter_inactive'), icon: XCircle },
    { id: 'obligatoire', name: p('filter_required'), icon: ToggleLeft },
  ];

  const attributeDistribution = {
    all_attributes: {
      title: p('dist_overview'),
      data: [
        { name: p('stat_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('stat_required'), count: stats.obligatoires, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { name: p('stat_usages'), count: stats.utilisations, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type'),
      data: DATA_TYPE_OPTIONS.map((opt, idx) => ({
        name: opt.label,
        count: attributesArray.filter((a) => a.data_type === opt.value).length,
        color: [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-amber-500 to-amber-600',
          'bg-gradient-to-r from-teal-500 to-teal-600',
        ][idx],
      })),
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { name: p('stat_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
  };

  const distributionInfo = attributeDistribution[mainViewMode] || attributeDistribution.all_attributes;

  const formatDate = (value) => {
    if (!value) return p('not_applicable');
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return p('not_applicable');
    return d.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const baseFilteredAttributes = useMemo(
    () =>
      attributesArray.filter((attr) => {
        if (dateRange.start || dateRange.end) {
          const createdDate = new Date(attr.created_at || 0);
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (createdDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (createdDate > endOfDay) return false;
          }
        }

        if (quickFilter === 'actif' && attr.status !== 'actif') return false;
        if (quickFilter === 'inactif' && attr.status !== 'inactif') return false;
        if (quickFilter === 'obligatoire' && !attr.is_required) return false;

        const term = searchTerm.toLowerCase();
        if (
          term &&
          !attr.name?.toLowerCase().includes(term) &&
          !attr.attribute_number?.toLowerCase().includes(term) &&
          !attr.data_type?.toLowerCase().includes(term) &&
          !attr.category_scope?.toLowerCase().includes(term)
        ) {
          return false;
        }

        return true;
      }),
    [attributesArray, dateRange, quickFilter, searchTerm],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('attribute_number', (r) => r.attribute_number || ''),
      textColumnRule('created_at', (r) => formatDate(r.created_at)),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('data_type', (r) => getDataTypeLabel(r.data_type)),
      textColumnRule('usage_count', (r) => String(parseInt(r.usage_count, 10) || 0)),
      textColumnRule('unit', (r) => r.unit || ''),
      textColumnRule('is_required', (r) => (r.is_required ? p('yes') : p('no'))),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [dateLocale, t, p],
  );

  const filteredAttributes = useMemo(
    () => applyColumnFilters(baseFilteredAttributes, colFilters, columnFilterRules),
    [baseFilteredAttributes, colFilters, columnFilterRules],
  );

  const filteredAttributeIds = useMemo(() => filteredAttributes.map((a) => a.id), [filteredAttributes]);
  const displayedAttributes = useMemo(
    () => sliceArticlesListItems(filteredAttributes, articlesConfig.itemsPerPage),
    [filteredAttributes, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredAttributeIds.length > 0 && filteredAttributeIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredAttributeIds);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const getDataTypeBadge = (dataType) => (
    <span
      className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize ${
        DATA_TYPE_BADGE[dataType] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
      }`}
    >
      {getDataTypeLabel(dataType)}
    </span>
  );

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'attribute_number', 'created_at', 'name', 'data_type', 'unit', 'is_required', 'usage_count', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'attribute_number', label: p('col_number'), checked: columnVisibility.attribute_number !== false, required: true },
      { key: 'created_at', label: p('col_date'), checked: columnVisibility.created_at !== false },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'data_type', label: p('col_type'), checked: columnVisibility.data_type !== false },
      { key: 'unit', label: p('col_unit'), checked: columnVisibility.unit !== false },
      { key: 'is_required', label: p('col_required'), checked: columnVisibility.is_required !== false },
      { key: 'usage_count', label: p('col_usages'), checked: columnVisibility.usage_count !== false },
      { key: 'status', label: p('col_status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: p('col_actions'), checked: true, required: true },
    ],
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Tag className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={() => setRefreshTrigger((pVal) => pVal + 1)} className="refresh-button" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { label: p('stat_total'), value: stats.total, icon: Tag, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_active'), value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_inactive'), value: stats.inactifs, icon: XCircle, color: 'from-red-500 to-red-600' },
          { label: p('stat_required'), value: stats.obligatoires, icon: ToggleLeft, color: 'from-purple-500 to-purple-600' },
          { label: p('stat_usages'), value: stats.utilisations, icon: BarChart3, color: 'from-amber-500 to-amber-600' },
        ], 4).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div
              className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}
              style={{ pointerEvents: 'none' }}
            />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}
                    style={{ pointerEvents: 'none' }}
                  />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setMainViewMode(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map((btn) => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {btn.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">
                {distributionInfo.title}
              </h3>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
                {distributionInfo.data.map((item, index) => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                        <span className="text-white font-semibold">
                          {item.count} ({percentage}%)
                        </span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('attribute_number') ? (
                    <TableColumnHeaderFilter columnKey="attribute_number" label={p('col_number')} value={colFilters.attribute_number ?? ''} onChange={(v) => setColFilter('attribute_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('created_at') ? (
                    <TableColumnHeaderFilter columnKey="created_at" label={p('col_date')} value={colFilters.created_at ?? ''} onChange={(v) => setColFilter('created_at', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('name') ? (
                    <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('data_type') ? (
                    <TableColumnHeaderFilter columnKey="data_type" label={p('col_type')} value={colFilters.data_type ?? ''} onChange={(v) => setColFilter('data_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('unit') ? <TableColumnHeaderFilter columnKey="unit" label={p('col_unit')} value={colFilters.unit ?? ''} onChange={(v) => setColFilter('unit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('is_required') ? <TableColumnHeaderFilter columnKey="is_required" label={p('col_required')} value={colFilters.is_required ?? ''} onChange={(v) => setColFilter('is_required', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  {showCol('usage_count') ? (
                    <TableColumnHeaderFilter columnKey="usage_count" label={p('col_usages')} value={colFilters.usage_count ?? ''} onChange={(v) => setColFilter('usage_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" />
                  ) : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      {p('loading')}
                    </td>
                  </tr>
                ) : displayedAttributes.length > 0 ? (
                  displayedAttributes.map((attr, index) => (
                    <tr
                      key={attr.id}
                      className={`hover:bg-white/5 transition-colors ${
                        selectedRecords.includes(attr.id) ? 'bg-white/10' : ''
                      }`}
                    >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(attr.id)} onChange={() => handleSelectRecord(attr.id)} />
                      {showCol('attribute_number') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold font-mono">
                              {attr.attribute_number || p('not_applicable')}
                            </span>
                            {attr.category_scope && (
                              <span className="text-white/50 text-xs mt-0.5">{attr.category_scope}</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {showCol('created_at') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {formatDate(attr.created_at)}
                            </div>
                            {attr.updated_at && (
                              <span className="text-white/50 text-xs mt-0.5">
                                {p('updated_prefix')} {formatDate(attr.updated_at)}
                              </span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4 text-white font-semibold">{attr.name}</td>
                      ) : null}
                      {showCol('data_type') ? (
                        <td className="px-6 py-4">
                          <div className="flex justify-start">{getDataTypeBadge(attr.data_type)}</div>
                        </td>
                      ) : null}
                      {showCol('unit') ? (
                        <td className="px-6 py-4 text-white/80">{attr.unit || p('empty_dash')}</td>
                      ) : null}
                      {showCol('is_required') ? (
                        <td className="px-6 py-4 text-center">
                          {attr.is_required ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                              {p('yes')}
                            </span>
                          ) : (
                            <span className="text-white/40 text-sm">{p('no')}</span>
                          )}
                        </td>
                      ) : null}
                      {showCol('usage_count') ? (
                        <td className="px-6 py-4 text-center">
                          <span className="text-white font-bold">{parseInt(attr.usage_count, 10) || 0}</span>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={attr.status}
                            recordId={attr.id}
                            options={STATUS_OPTIONS}
                            onChange={handleStatusChange}
                            title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(attr)}
                          onEdit={() => handleEdit(attr)}
                          onDelete={() => handleDelete(attr.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Tag size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedAttributes.length > 0 ? (
              displayedAttributes.map((attr, index) => (
                <motion.div
                  key={attr.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Tag size={20} className="text-blue-400" />
                          {attr.name}
                        </h3>
                        {getDataTypeBadge(attr.data_type)}
                        <QawaemClickableStatusBadge
                          status={attr.status}
                          recordId={attr.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80 font-mono">
                          {attr.attribute_number}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                        <div>
                          <span className="font-medium text-white/50">{p('card_unit')}</span>{' '}
                          {attr.unit || p('empty_dash')}
                        </div>
                        <div>
                          <span className="font-medium text-white/50">{p('card_required')}</span>{' '}
                          {attr.is_required ? p('yes') : p('no')}
                        </div>
                        <div>
                          <span className="font-medium text-white/50">{p('card_usages')}</span>{' '}
                          {parseInt(attr.usage_count, 10) || 0}
                        </div>
                        <div>
                          <span className="font-medium text-white/50">{p('card_created')}</span>{' '}
                          {formatDate(attr.created_at)}
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(attr)}
                      onEdit={() => handleEdit(attr)}
                      onDelete={() => handleDelete(attr.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                      className="ml-4"
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Tag size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={resetForm}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1200px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Tag className="h-6 w-6 text-blue-400" />
                  {editingRecord ? p('modal_edit') : p('modal_new')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="attribute-form"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <CheckCircle size={18} />
                    {editingRecord ? p('btn_update') : p('btn_create')}
                  </button>
                  <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="shrink-0 px-6 pt-3 border-b border-white/10">
                <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingRecord ? p('modal_edit') : p('modal_new')}>
                  {formTabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const active = formTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setFormTab(tab.id)}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                          active
                            ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                            : 'text-white/55 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <TabIcon size={16} className={active ? (tab.id === 'values' ? 'text-blue-400' : tab.id === 'scope_notes' ? 'text-amber-400' : 'text-emerald-400') : ''} />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="attribute-form" onSubmit={handleSubmit}>
                  {formTab === 'base' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                          <RequiredLabelText text={p('field_name')} required />
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('field_name_ph')}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_type')}</label>
                        <CustomSelect
                          value={formData.data_type}
                          onChange={(val) => setFormData({ ...formData, data_type: val })}
                          options={DATA_TYPE_OPTIONS}
                          placeholder={p('field_type_ph')}
                          searchable={false}
                          creatable={false}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_unit')}</label>
                        <input
                          type="text"
                          value={formData.unit}
                          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('field_unit_ph')}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.status')}</label>
                        <CustomSelect
                          value={formData.status}
                          onChange={(val) => setFormData({ ...formData, status: val })}
                          options={STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
                          placeholder={p('field_status_ph')}
                          searchable={false}
                          creatable={false}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_required')}</label>
                        <CustomSelect
                          value={formData.is_required ? 'yes' : 'no'}
                          onChange={(val) => setFormData({ ...formData, is_required: val === 'yes' })}
                          options={[
                            { value: 'no', label: p('no') },
                            { value: 'yes', label: p('yes') },
                          ]}
                          searchable={false}
                          creatable={false}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_usages')}</label>
                        <CustomNumberInput
                          step={1}
                          value={formData.usage_count}
                          onChange={(v) => setFormData({ ...formData, usage_count: v })}
                          placeholder="0"
                        />
                      </div>
                      {formData.data_type === 'liste' ? (
                        <div className="min-w-0 md:col-span-3">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_options')}</label>
                          <textarea
                            value={formData.options_json}
                            onChange={(e) => setFormData({ ...formData, options_json: e.target.value })}
                            rows={2}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={p('field_options_ph')}
                          />
                        </div>
                      ) : null}
                    </div>
                  )}

                  {formTab === 'scope_notes' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="min-w-0">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_category_scope')}</label>
                        <input
                          type="text"
                          value={formData.category_scope}
                          onChange={(e) => setFormData({ ...formData, category_scope: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('field_category_scope_ph')}
                        />
                      </div>
                      <div className="min-w-0 md:col-span-2">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('field_notes_ph')}
                        />
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {showViewModal &&
        viewingRecord &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '95%',
                maxWidth: '1100px',
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Tag className="h-6 w-6 text-blue-400" />
                  {p('modal_view')}
                </h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Hash size={18} className="text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_base')}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_attr_number')}</p>
                      <p className="text-white font-semibold font-mono">{viewingRecord.attribute_number || p('not_applicable')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_name')}</p>
                      <p className="text-white font-semibold">{viewingRecord.name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_type')}</p>
                      {getDataTypeBadge(viewingRecord.data_type)}
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_required')}</p>
                      <p className="text-white font-semibold">{viewingRecord.is_required ? p('yes') : p('no')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                      <Ruler size={18} className="text-blue-400" />
                      <h3 className="text-lg font-semibold text-white">{p('view_values')}</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_unit')}</p>
                        <p className="text-white font-semibold">{viewingRecord.unit || p('empty_dash')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_usages')}</p>
                        <p className="text-white font-bold text-xl">
                          {parseInt(viewingRecord.usage_count, 10) || 0}
                        </p>
                      </div>
                      {viewingRecord.options_json && (
                        <div>
                          <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_options')}</p>
                          <p className="text-white/80 text-sm font-mono break-all">{viewingRecord.options_json}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                      <Layers size={18} className="text-amber-400" />
                      <h3 className="text-lg font-semibold text-white">{p('section_scope')}</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_scope')}</p>
                        <p className="text-white font-semibold">{viewingRecord.category_scope || p('empty_dash')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('field_status')}</p>
                        {getStatusBadge(viewingRecord.status)}
                      </div>
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_created')}</p>
                        <div className="flex items-center gap-1.5 text-white font-semibold">
                          <Clock size={14} className="text-white/50" />
                          {formatDate(viewingRecord.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {viewingRecord.notes && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-3">
                      <AlignLeft size={18} className="text-purple-400" />
                      <h3 className="text-lg font-semibold text-white">{p('section_notes')}</h3>
                    </div>
                    <p className="text-white/70 text-sm italic">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowViewModal(false)}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {p('btn_close')}
              </button>
            </motion.div>
          </div>,
          document.body
        )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default AttributsPage;
